(function (root, factory) {
  var api = factory();
  if (typeof module === "object" && module.exports) {
    module.exports = api;
  }
  root.AgoPostcodeSourceGate = api;
})(typeof window !== "undefined" ? window : globalThis, function () {
  "use strict";

  var SLOT_KEYS = ["AM", "PM", "DAY"];
  var COVERAGE = {
    sydneyMinimum: 150,
    canberraMinimum: 10,
    wollongongMinimum: 5,
    deliverableMinimum: 165,
  };
  var DEFAULTS = {
    primaryTimeoutMs: 10000,
    remoteTimeoutMs: 8000,
    maxRemoteAgeMs: 30 * 24 * 60 * 60 * 1000,
    futureSkewMs: 5 * 60 * 1000,
  };

  function gateError(code) {
    var error = new Error(code);
    error.gateCode = code;
    return error;
  }

  function classifyError(error) {
    var code = error && error.gateCode;
    var allowed = [
      "timeout",
      "http_error",
      "network_error",
      "invalid_json",
      "invalid_payload",
      "invalid_snapshot",
      "future_remote",
      "stale_remote",
      "remote_older_than_bundled",
    ];
    return allowed.indexOf(code) >= 0 ? code : "network_error";
  }

  function isRecord(value) {
    return !!value && typeof value === "object" && !Array.isArray(value);
  }

  function unique(values) {
    return values.filter(function (value, index) {
      return values.indexOf(value) === index;
    });
  }

  function normalizeDays(value) {
    if (typeof value === "undefined") return undefined;
    if (!Array.isArray(value)) throw gateError("invalid_payload");
    var days = value
      .map(function (day) {
        return String(day).trim();
      })
      .filter(Boolean);
    if (!days.length || days.some(function (day) { return !/^[1-7]$/.test(day); })) {
      throw gateError("invalid_payload");
    }
    return unique(days);
  }

  function normalizeRegion(value) {
    if (!isRecord(value)) throw gateError("invalid_payload");
    var normalized = {};
    Object.keys(value).forEach(function (postcode) {
      var rawInfo = value[postcode];
      if (!/^\d{4}$/.test(postcode) || !isRecord(rawInfo)) {
        throw gateError("invalid_payload");
      }
      var info = {};
      SLOT_KEYS.forEach(function (slotKey) {
        var days = normalizeDays(rawInfo[slotKey]);
        if (days) info[slotKey] = days;
      });
      if (Object.keys(info).length) normalized[postcode] = info;
    });
    return normalized;
  }

  function normalizeBlacklist(value) {
    if (!Array.isArray(value)) throw gateError("invalid_payload");
    return unique(value.map(function (postcode) {
      var normalized = String(postcode);
      if (!/^\d{4}$/.test(normalized)) throw gateError("invalid_payload");
      return normalized;
    }));
  }

  function regionCounts(payload) {
    var blacklist = new Set((payload.blacklist || []).map(String));
    var deliverable = new Set(
      Object.keys(payload.sydney || {})
        .concat(Object.keys(payload.canberra || {}))
        .concat(Object.keys(payload.wollongong || {})),
    );
    blacklist.forEach(function (postcode) {
      deliverable.delete(postcode);
    });
    return {
      sydney: Object.keys(payload.sydney || {}).length,
      canberra: Object.keys(payload.canberra || {}).length,
      wollongong: Object.keys(payload.wollongong || {}).length,
      deliverable: deliverable.size,
    };
  }

  function validatePayload(value) {
    if (!isRecord(value)) throw gateError("invalid_payload");
    var payload = {
      sydney: normalizeRegion(value.sydney),
      canberra: normalizeRegion(value.canberra),
      wollongong: normalizeRegion(value.wollongong),
      blacklist: normalizeBlacklist(value.blacklist),
    };
    var counts = regionCounts(payload);
    if (
      counts.sydney < COVERAGE.sydneyMinimum ||
      counts.canberra < COVERAGE.canberraMinimum ||
      counts.wollongong < COVERAGE.wollongongMinimum ||
      counts.deliverable < COVERAGE.deliverableMinimum
    ) {
      throw gateError("invalid_payload");
    }
    return payload;
  }

  function validateSnapshot(value) {
    if (!isRecord(value) || value.schemaVersion !== 1) throw gateError("invalid_snapshot");
    if (typeof value.generatedAt !== "string" || Number.isNaN(Date.parse(value.generatedAt))) {
      throw gateError("invalid_snapshot");
    }
    if (typeof value.source !== "string" || !value.source.trim()) {
      throw gateError("invalid_snapshot");
    }
    return {
      schemaVersion: 1,
      generatedAt: value.generatedAt,
      source: value.source,
      payload: validatePayload(value.payload),
    };
  }

  function validateRemoteSnapshot(value, bundledSnapshot, nowMs, options) {
    var snapshot = validateSnapshot(value);
    var generatedMs = Date.parse(snapshot.generatedAt);
    var bundledMs = bundledSnapshot ? Date.parse(bundledSnapshot.generatedAt) : null;
    if (generatedMs > nowMs + options.futureSkewMs) throw gateError("future_remote");
    if (nowMs - generatedMs > options.maxRemoteAgeMs) throw gateError("stale_remote");
    if (bundledMs !== null && generatedMs < bundledMs) throw gateError("remote_older_than_bundled");
    return snapshot;
  }

  function deepFreeze(value) {
    if (!value || typeof value !== "object" || Object.isFrozen(value)) return value;
    Object.keys(value).forEach(function (key) {
      deepFreeze(value[key]);
    });
    return Object.freeze(value);
  }

  function clone(value) {
    return JSON.parse(JSON.stringify(value));
  }

  function ageBucket(generatedAt, nowMs) {
    if (!generatedAt) return "not_applicable";
    var ageMs = nowMs - Date.parse(generatedAt);
    if (ageMs < 0) return "future";
    if (ageMs < 24 * 60 * 60 * 1000) return "lt_1d";
    if (ageMs <= 7 * 24 * 60 * 60 * 1000) return "1_7d";
    if (ageMs <= 30 * 24 * 60 * 60 * 1000) return "8_30d";
    return "gt_30d";
  }

  function pushTelemetry(rootObject, result, failures, nowMs) {
    var counts = result.payload ? regionCounts(result.payload) : {
      sydney: 0,
      canberra: 0,
      wollongong: 0,
      deliverable: 0,
    };
    var signal = {
      event: "app_health_signal",
      signal_name: result.source === "unavailable"
        ? "delivery_postcode_unavailable"
        : "delivery_postcode_source",
      surface: "web",
      source: result.source,
      failure_class: failures.length ? failures.join("|") : "none",
      snapshot_age_bucket: ageBucket(result.generatedAt, nowMs),
      sydney_count: counts.sydney,
      canberra_count: counts.canberra,
      wollongong_count: counts.wollongong,
    };
    rootObject.dataLayer = rootObject.dataLayer || [];
    rootObject.dataLayer.push(signal);
  }

  function fetchJson(fetchImpl, url, timeoutMs) {
    var controller = typeof AbortController === "function" ? new AbortController() : null;
    var timer;
    var timeoutPromise = new Promise(function (_resolve, reject) {
      timer = setTimeout(function () {
        if (controller) controller.abort();
        reject(gateError("timeout"));
      }, timeoutMs);
    });
    var requestPromise = Promise.resolve()
      .then(function () {
        return fetchImpl(url, controller ? { signal: controller.signal } : {});
      })
      .catch(function (error) {
        if (error && error.gateCode) throw error;
        throw gateError("network_error");
      })
      .then(function (response) {
        if (!response || !response.ok) throw gateError("http_error");
        return Promise.resolve(response.json()).catch(function () {
          throw gateError("invalid_json");
        });
      });
    return Promise.race([requestPromise, timeoutPromise]).finally(function () {
      clearTimeout(timer);
    });
  }

  function commit(rootObject, result) {
    var payload = deepFreeze(clone(result.payload));
    var merged = Object.assign({}, payload.sydney, payload.canberra, payload.wollongong);
    payload.blacklist.forEach(function (postcode) {
      delete merged[String(postcode)];
    });
    var activeMap = deepFreeze(merged);
    rootObject.sydneyPostcodeData = payload.sydney;
    rootObject.canberraPostcodeData = payload.canberra;
    rootObject.wollongongPostcodeData = payload.wollongong;
    rootObject.umallCalanderBlacklistPostcode = payload.blacklist;
    rootObject.umallCalanderPostcode = activeMap;
    rootObject.umallPostcodeSourceState = deepFreeze({
      status: "ready",
      source: result.source,
      generatedAt: result.generatedAt || null,
      counts: regionCounts(payload),
    });
    return deepFreeze({
      status: "ready",
      source: result.source,
      generatedAt: result.generatedAt || null,
      payload: payload,
      activeMap: activeMap,
    });
  }

  async function resolveAndCommit(config) {
    var rootObject = config.root || (typeof window !== "undefined" ? window : globalThis);
    var fetchImpl = config.fetchImpl || rootObject.fetch.bind(rootObject);
    var nowMs = typeof config.nowMs === "number" ? config.nowMs : Date.now();
    var options = Object.assign({}, DEFAULTS, config.options || {});
    var failures = [];
    var bundledSnapshot;
    var bundledFailure = null;
    rootObject.umallCalanderPostcode = undefined;
    rootObject.sydneyPostcodeData = undefined;
    rootObject.canberraPostcodeData = undefined;
    rootObject.wollongongPostcodeData = undefined;
    rootObject.umallCalanderBlacklistPostcode = undefined;
    rootObject.umallPostcodeSourceState = deepFreeze({ status: "loading", source: null });

    try {
      bundledSnapshot = validateSnapshot(config.bundledSnapshot);
    } catch (error) {
      bundledFailure = classifyError(error);
      bundledSnapshot = null;
    }

    try {
      var primaryRaw = await fetchJson(fetchImpl, config.primaryUrl, options.primaryTimeoutMs);
      var primaryPayload = validatePayload(primaryRaw);
      var primaryResult = { source: "primary_api", generatedAt: null, payload: primaryPayload };
      var primaryCommitted = commit(rootObject, primaryResult);
      pushTelemetry(rootObject, primaryResult, failures, nowMs);
      return primaryCommitted;
    } catch (error) {
      failures.push(classifyError(error));
    }

    try {
      var remoteRaw = await fetchJson(fetchImpl, config.remoteUrl, options.remoteTimeoutMs);
      var remoteSnapshot = validateRemoteSnapshot(remoteRaw, bundledSnapshot, nowMs, options);
      var remoteResult = {
        source: "remote_lkg_fallback",
        generatedAt: remoteSnapshot.generatedAt,
        payload: remoteSnapshot.payload,
      };
      var remoteCommitted = commit(rootObject, remoteResult);
      pushTelemetry(rootObject, remoteResult, failures, nowMs);
      return remoteCommitted;
    } catch (error) {
      failures.push(classifyError(error));
    }

    if (bundledSnapshot) {
      var bundledResult = {
        source: "bundled_snapshot",
        generatedAt: bundledSnapshot.generatedAt,
        payload: bundledSnapshot.payload,
      };
      var bundledCommitted = commit(rootObject, bundledResult);
      pushTelemetry(rootObject, bundledResult, failures, nowMs);
      return bundledCommitted;
    }

    if (bundledFailure) failures.push(bundledFailure);

    var unavailable = deepFreeze({ status: "unavailable", source: "unavailable" });
    rootObject.umallPostcodeSourceState = unavailable;
    pushTelemetry(rootObject, unavailable, failures, nowMs);
    throw gateError("invalid_snapshot");
  }

  return {
    COVERAGE: COVERAGE,
    DEFAULTS: DEFAULTS,
    ageBucket: ageBucket,
    classifyError: classifyError,
    regionCounts: regionCounts,
    resolveAndCommit: resolveAndCommit,
    validatePayload: validatePayload,
    validateSnapshot: validateSnapshot,
  };
});
